import React, { useEffect } from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Container } from "reactstrap";

import { Api } from "@api/index";
import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { setTitle } from "@app/util";
import { messages } from "@app/messages";
import { GlobalState } from "@app/models/state";
import { Track as TrackEl } from "@music/components/Track";
import { Album, Track } from "@music/models";
import { messages as mMessages } from "@music/messages";
import {
    PropsWithId,
    makeGetTrack,
    canViewAlbum as canViewAlbumSelector,
    makeGetMappingForTrack,
} from "@music/selectors";
import {
    canViewSales as canViewSalesSelector,
    canViewStreamDemographics as canViewStreamDemographicsSelector,
    canViewPlaylistTrends as canViewPlaylistTrendsSelector,
 } from "@sales/selectors";
import { DemographicsCharts } from "@sales/components/DemographicsCharts";
import { GeoSalesChart } from "@sales/components/GeoSalesChart";
import { Sales } from "@sales/components/Sales";
import { AlbumSalesTable } from "@sales/components/AlbumSalesTable";
import { PlaylistTrendsList } from "@sales/components/PlaylistTrendsList";
import { messages as sMessages } from "@sales/messages";
import { BreadcrumbsItem } from "@ui/Breadcrumbs";
import { Page, PageTitle } from "@ui/Page";
import { compose } from "@util/compose";
import { trackNotFound, HasTrack } from "@app/components/notFoundHocs";

export interface TracksViewProps extends HasTrack, ApiConnectedOwnProps, RouteComponentProps<any> {
    canViewSales: boolean;
    canViewAlbum: boolean;
    canViewStreamDemographics: boolean;
    canViewPlaylistTrends: boolean;
    mappedId: number;
}

const TracksView = ({
    track,
    canViewSales,
    canViewStreamDemographics,
    canViewAlbum,
    canViewPlaylistTrends,
    history,
    mappedId,
}: TracksViewProps) => {
    const intl = useIntl();
    useEffect(() => {
        setTitle(intl, messages.pageTracksTitle, track.title);
    });

    // Redirect to mapped track if there is one
    if (mappedId != null) {
        const mappedTrack = new Track({ id: mappedId });
        history.push(mappedTrack.getViewUrl());
    }

    const breadcrumbItems: BreadcrumbsItem[] = [{ text: track.title}];
    return (
        <Page fluid className="tracks-view-page" breadcrumbs={breadcrumbItems}>
            <div className="tracks-view-page__details">
                <TrackEl
                    track={track}
                    album={null}
                    link={false}
                    style="headline"
                    additionalDetails={true}
                />
            </div>

            <Container>
                {canViewSales && <Sales trackId={track.id} />}

                {canViewAlbum && canViewSales &&
                    <PageTitle className="display-3 mb-5" tag="h5">
                        <FormattedMessage {...mMessages.albumEntityPlural} />
                    </PageTitle>}
                {canViewAlbum && canViewSales &&
                    <AlbumSalesTable trackId={track.id}
                        albums={track.albums as Album[]}
                    />}
                {canViewSales && <GeoSalesChart trackId={track.id} />}
                {canViewPlaylistTrends && (
                    <>
                        <PageTitle className="display-3 mb-5" tag="h5">
                            <FormattedMessage {...sMessages.playlistTrendsTitle} />
                        </PageTitle>
                        <PlaylistTrendsList trackId={track.id} />
                    </>
                )}
                {canViewStreamDemographics &&
                    <DemographicsCharts trackId={track.id} />
                }
            </Container>
        </Page>
    );
};

// Map the users state to the component.
const makeMapStateToProps = () => {
    const getTrack = makeGetTrack();
    const getMappingForTrack = makeGetMappingForTrack();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const track = getTrack(state, ownProps);
        const mappedId = getMappingForTrack(state, ownProps);
        // Build actionCreators and lastLoaded based on the track.
        const lastLoaded: Date[] = [ null ];
        const actionCreators = [ makeTracksShow(Number(ownProps.params.id)) ];
        const requestId = [ "track_" + ownProps.params.id ];
        if (track != null) {
            lastLoaded[0] = track.lastLoaded;
            // Also ensure the albums are loaded.
            if (track.albums != null) {
                for (const album of track.albums) {
                    if (album != null && album instanceof Album) {
                        lastLoaded.push(album.lastLoaded);
                        actionCreators.push(makeAlbumsShow(album.id));
                        requestId.push("album_" + album.id);
                    }
                }
            }
        }
        return {
            track,
            mappedId,
            lastLoaded,
            actionCreators,
            requestId,
            canViewSales: canViewSalesSelector(state),
            canViewAlbum: canViewAlbumSelector(state),
            canViewStreamDemographics: canViewStreamDemographicsSelector(state),
            canViewPlaylistTrends: canViewPlaylistTrendsSelector(state),
        } as TracksViewProps;
    };
};

const makeTracksShow = (id: number) => () => {
    return Api.tracksShow(id);
};

const makeAlbumsShow = (id: number) => () => {
    return Api.albumsShow(id);
};

const ConnectedTracksView = compose<TracksViewProps, PropsWithId>(
    connect(makeMapStateToProps, null),
    connectToApi({
        showRefresh: false,
    }),
    trackNotFound,
    withRouter,
)(TracksView);

export {
    TracksView as TracksViewPresenter,
    ConnectedTracksView as TracksView,
};
